/** @jsxImportSource @emotion/react */
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { initSignatureRc } from '@/api/rcApi';
import { appError, appRedirect, appSuccess } from '@/redux/actions/appActions';
import { trackEvent } from '@/utils/trackEvent';
import { instance as axios } from '@/api/baseUrl';
import { trackDatadogAction, trackDatadogError } from '@/services/datadog';

const Spacing = styled.div`
  height: 400px;
  @media (max-width: 600px) {
    height: 200px;
  }
`;

const RcSuccessPaymentPage: React.FC = () => {
  const dispatch = useAppDispatch();

  const reduxContractReference = useAppSelector((state) => state.rc.contractReference);
  const contractReference = reduxContractReference || localStorage.getItem('rc_contractReference');

  const userEmail = useAppSelector(state => state.user?.email);
  const userFirstname = useAppSelector(state => state.user?.firstname);
  const userLastname = useAppSelector(state => state.user?.lastname);

  const signatureCalledRef = React.useRef(false);
  const isMountedRef = React.useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!contractReference || signatureCalledRef.current) return;

    signatureCalledRef.current = true;
    dispatch(appRedirect());

    const timer = setTimeout(async () => {
      if (!isMountedRef.current) return;

      trackDatadogAction('rc_stripe_redirect_success', {
        contractReference,
        source: reduxContractReference ? 'redux' : 'localStorage',
      });

      // Refresh token après redirect Stripe
      try {
        await axios.post('/security/token/refresh');
        trackDatadogAction('rc_token_refresh_success', { contractReference });
      } catch (refreshError) {
        console.log('Token refresh after Stripe redirect:', refreshError);
        trackDatadogError(refreshError as Error, {
          action: 'rc_token_refresh_failed',
          contractReference,
        });
      }

      initSignatureRc(contractReference)
        .then(res => {
          if (!isMountedRef.current) return;

          if (res.data.signatureUrl) {
            trackDatadogAction('rc_init_signature_success', {
              contractReference: res.data.contractReference,
              customerNumber: res.data.customerNumber,
            });

            // eslint-disable-next-line react-hooks/exhaustive-deps
            trackEvent('purchase', {
              ecommerce: {
                value: res.data.stripePrice,
                currency: 'EUR',
                payment_type: 'card',
                items: [{
                  item_category: 'RC',
                  price: res.data.stripePrice,
                  quantity: 1
                }]
              },
              user_data: {
                email: userEmail,
                first_name: userFirstname,
                last_name: userLastname,
              },
              contract_info: {
                contract_id: res.data.contractReference,
                customer_number: res.data.customerNumber,
                session_stripe: res.data.sessionStripe,
                start_date: res.data.startDate,
                end_date: res.data.endDate,
              }
            });

            dispatch(appSuccess());
            window.location.href = res.data.signatureUrl;
          } else {
            trackDatadogError(new Error('No signatureUrl in response'), {
              action: 'rc_init_signature_no_url',
              contractReference,
            });
            dispatch(appError());
            console.error('Erreur lien signature Sell&Sign RC:');
          }
        })
        .catch(err => {
          if (!isMountedRef.current) return;

          trackDatadogError(err, {
            action: 'rc_init_signature_failed',
            contractReference,
          });
          dispatch(appError());
          console.error('Erreur initSignature RC:', err);
        });
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractReference, dispatch]);

  return (
    <Spacing />
  );
};

export default RcSuccessPaymentPage;
